import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { SignaturePad } from "@/components/SignaturePad";
import { toast, Toaster } from "sonner";
import { CheckCircle, AlertCircle, Clock, FileText, User, Building, Download, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PhysicianSignPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [agencyData, setAgencyData] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [npi, setNpi] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailNote, setEmailNote] = useState("");

  useEffect(() => {
    verifyLink();
  }, [token]);

  const verifyLink = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/physician-sign/verify/${token}`);
      setLinkData(response.data.link);
      setDocumentData(response.data.document);
      setAgencyData(response.data.agency);
      
      if (response.data.link.status === "signed") {
        setSigned(true);
      }
    } catch (err) {
      console.error("Failed to verify link:", err);
      if (err.response?.status === 404) {
        setError("This link is invalid or has been revoked.");
      } else if (err.response?.status === 410) {
        setError("This link has expired. Please contact the agency for a new link.");
      } else {
        setError("An error occurred. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (signatureData) => {
    setSigning(true);
    try {
      const response = await axios.post(`${API}/physician-sign/submit/${token}`, {
        signature: signatureData,
        npi: npi
      });
      toast.success("Document signed successfully!");
      setSigned(true);
      // Store email status for display
      setEmailSent(response.data.email_sent);
      setEmailNote(response.data.email_note);
    } catch (err) {
      console.error("Failed to sign:", err);
      toast.error(err.response?.data?.detail || "Failed to submit signature");
    } finally {
      setSigning(false);
    }
  };

  // Public, token-scoped PDF — physicians are not logged in, so the regular /pdf routes would 401
  const publicPdfUrl = ["cms485", "md_order"].includes(linkData?.document_type) ? `${API}/physician-sign/verify/${token}/pdf` : null;

  const handleDownloadPDF = () => {
    if (publicPdfUrl) {
      window.open(publicPdfUrl, '_blank');
    } else {
      toast.error("PDF download not available for this document type");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Verifying secure link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-red-600">Link Invalid</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <CardTitle className="text-emerald-600">Document Signed</CardTitle>
            <CardDescription>
              Thank you, Dr. {linkData?.physician_name}. Your signature has been recorded.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-sm text-slate-500">
              <p>Signed on: {new Date().toLocaleString()}</p>
            </div>
            
            {/* Email confirmation */}
            {emailSent && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-blue-800">
                  A copy has been sent to your email on file.
                </p>
              </div>
            )}
            
            {/* Download PDF button */}
            <div className="pt-2">
              <Button 
                className="w-full" 
                variant="outline"
                onClick={handleDownloadPDF}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Signed Copy (PDF)
              </Button>
            </div>
            
            <p className="text-center text-xs text-slate-400 pt-2">
              You may close this window after downloading your copy.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <Toaster position="top-center" />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">
            {agencyData?.name || "Healthcare Agency"}
          </h1>
          <p className="text-slate-500">Secure Physician Signature Request</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Document Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Document Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-xs text-slate-500">Document Type</Label>
                <p className="font-semibold">
                  {linkData?.document_type === "cms485" ? "CMS 485 - Home Health Certification" : 
                   linkData?.document_type === "md_order" ? "Physician Order Form" :
                   "Plan of Care (PCW/Caregiver)"}
                </p>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-xs text-slate-500">Patient</Label>
                <p className="font-semibold">{linkData?.client_name || "N/A"}</p>
              </div>

              {publicPdfUrl && (
                <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-100" data-testid="sign-link-pdf-preview">
                  <iframe title="Document preview" src={`${publicPdfUrl}#toolbar=0&view=FitH`} className="w-full h-[70vh]" />
                </div>
              )}
              {documentData && linkData?.document_type === "cms485" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Certification Period</Label>
                      <p className="text-sm">
                        {documentData.certification_period_from} to {documentData.certification_period_to}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Prognosis</Label>
                      <p className="text-sm capitalize">{documentData.prognosis || "N/A"}</p>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Principal Diagnosis</Label>
                    <p className="text-sm">{documentData.principal_diagnosis || "N/A"}</p>
                  </div>
                </>
              )}

              {documentData && linkData?.document_type === "care_plan_poc" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Hours/Week</Label>
                      <p className="text-sm">{documentData.hours_per_week || "N/A"}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">SOC Date</Label>
                      <p className="text-sm">{documentData.soc_date || "N/A"}</p>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Chronic Health Problems</Label>
                    <p className="text-sm">{documentData.chronic_health_problems || "N/A"}</p>
                  </div>
                </>
              )}

              {documentData && linkData?.document_type === "md_order" && (
                <>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Order Types</Label>
                    <p className="text-sm font-medium capitalize">
                      {documentData.order_types?.map(t => t.replace(/_/g, ' ')).join(', ') || "N/A"}
                    </p>
                  </div>
                  {documentData.client && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Primary Diagnosis</Label>
                      <p className="text-sm">{documentData.client.primary_diagnosis || "N/A"}</p>
                    </div>
                  )}
                  {documentData.notes && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Notes</Label>
                      <p className="text-sm">{documentData.notes}</p>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                <Clock className="w-4 h-4" />
                <span>
                  Link expires: {linkData?.expires_at ? new Date(linkData.expires_at).toLocaleDateString() : "N/A"}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Signature Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Physician Signature
              </CardTitle>
              <CardDescription>
                {linkData?.physician_name}, please sign below to certify this document.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  By signing below, I certify that I have reviewed this document and the information is accurate to the best of my knowledge.
                </p>
              </div>

              {/* NPI field for MD Orders */}
              {linkData?.document_type === "md_order" && (
                <div className="space-y-2">
                  <Label htmlFor="npi">NPI Number (Optional)</Label>
                  <input
                    id="npi"
                    type="text"
                    placeholder="1234567890"
                    value={npi}
                    onChange={(e) => setNpi(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Your Signature</Label>
                <SignaturePad
                  onSave={handleSign}
                  width={400}
                  height={200}
                  disabled={signing}
                />
              </div>

              {signing && (
                <div className="text-center text-slate-500">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-sm mt-2">Submitting signature...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>This is a secure signature request from {agencyData?.name || "the healthcare agency"}.</p>
          <p className="mt-1">If you did not expect this request, please contact the agency directly.</p>
        </div>
      </div>
    </div>
  );
}
